import React, { MutableRefObject, useRef, useState } from 'react';

import { Box, Grid, IconButton, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

import Player from 'video.js/dist/types/player';

import PlayerMessage from '../../../../features/LiveView/components/PlayerMessage';

import { useVideoRetry } from '../../../../hooks/useVideoRetry';
import TranslationHelper from '../../../../helpers/TranslationHelper';

import { PLAYBACK_RATES } from '../../../../constants/videoPlayerSettings';

import VideoJSPlayer from '../../../../components/VideoJSPlayer';

interface IOwnProps {
    cameraName: string;
    videoSrc: string;
    onPlayerReady: (player: Player) => void;
    videoRef?: MutableRefObject<HTMLVideoElement | null>;
    type: string;
    fullscreen: boolean;
}

const SinglePlayerForAllCameras = ({
    cameraName,
    videoSrc,
    onPlayerReady,
    videoRef,
    type,
    fullscreen,
}: IOwnProps) => {
    const internalVideoRef = useRef<HTMLVideoElement | null>(null);
    const playerRef = React.useRef<Player | null>(null);
    const [error, setError] = useState(false);

    const handleError = () => {
        playerRef.current && onPlayerReady(playerRef.current);
        setError(true);
    };

    const { handleRetryOnSrcError } = useVideoRetry({
        playerRef,
        videoSrc,
        onFail: handleError,
        type,
    });

    const videoJsOptions = {
        autoplay: 'muted',
        muted: true,
        playbackRates: PLAYBACK_RATES,
        controlBar: {
            children: ['volumePanel', 'fullscreenToggle'],
        },

        controls: true,
        fluid: true,
        sources: [
            {
                src: videoSrc,
                type,
            },
        ],
        className: 'vjs-control-bar-all-cameras',
        userActions: {
            click: false,
        },
    };

    const resetVideo = () => {
        setError(false);
    };

    const handleOnLoad = () => {
        if (playerRef.current) {
            //@ts-ignore
            playerRef.current.controlBar
                ?.el()
                ?.classList.add('vjs-control-bar-all-cameras');
            onPlayerReady(playerRef.current);
        }
    };
    return (
        <Grid
            item
            xs={fullscreen ? 26 : 36}
            lg={fullscreen ? 13 : 18}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: fullscreen ? '30vh' : undefined,
                border: '1px solid gray',
            }}
        >
            <Typography sx={{ color: 'white', alignSelf: 'center' }}>
                {TranslationHelper.translate(cameraName)}
            </Typography>
            {!error ? (
                <Box>
                    <VideoJSPlayer
                        options={videoJsOptions}
                        videoRef={videoRef ?? internalVideoRef}
                        playerRef={playerRef}
                        handleError={() => handleError()}
                        handleSrcError={handleRetryOnSrcError}
                        handleOnLoad={handleOnLoad}
                    />
                </Box>
            ) : (
                <PlayerMessage
                    message="The stream cannot be played"
                    typographySx={{ color: 'white' }}
                >
                    <IconButton
                        onClick={() => resetVideo()}
                        sx={{ color: 'white' }}
                    >
                        <RefreshIcon sx={{ color: 'white' }} />
                    </IconButton>
                </PlayerMessage>
            )}
        </Grid>
    );
};

export default SinglePlayerForAllCameras;
